import React, { useState, useEffect } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';
import { TimeSlot } from '../../types/booking';

interface NewSlot {
  date: string;
  startTime: string;
  endTime: string;
  amount?: number;
  currency?: string;
}

interface RecurringSlotConfig {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  amount?: number;
  currency?: string;
  daysOfWeek: number[]; // 0 = Sunday, 1 = Monday, etc.
}

export function TimeSlotManagement() {
  const { t } = useTranslation();
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** 0 = single slot, 1 = bulk, 2 = recurring */
  const [formTab, setFormTab] = useState(0);
  
  // Single slot form
  const [newSlot, setNewSlot] = useState<NewSlot>({
    date: '',
    startTime: '',
    endTime: '',
    amount: 50,
    currency: 'USD',
  });

  // Bulk slots form
  const [bulkSlots, setBulkSlots] = useState<NewSlot[]>([
    { date: '', startTime: '', endTime: '', amount: 50, currency: 'USD' }
  ]);

  // Recurring slots form
  const [recurringConfig, setRecurringConfig] = useState<RecurringSlotConfig>({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    amount: 50,
    currency: 'USD',
    daysOfWeek: [],
  });

  const daysOfWeekNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    loadTimeSlots();
  }, []);

  const showMessage = (message: string, isError = false) => {
    if (isError) {
      setError(message);
      setSuccess(null);
    } else {
      setSuccess(message);
      setError(null);
    }
    
    // Clear message after 5 seconds
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 5000);
  };

  const loadTimeSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.timeSlots.getMyTimeSlots();
      setTimeSlots(response);
    } catch (err: any) {
      console.error('Failed to load time slots:', err);
      showMessage(err.response?.data?.error || err.message || 'Failed to load time slots', true);
    } finally {
      setLoading(false);
    }
  };

  const validateTimeSlot = (slot: NewSlot): string | null => {
    if (!slot.date || !slot.startTime || !slot.endTime) {
      return 'All fields are required';
    }

    const startDateTime = new Date(`${slot.date}T${slot.startTime}`);
    const endDateTime = new Date(`${slot.date}T${slot.endTime}`);
    const now = new Date();

    const minimumStartTime = new Date(now.getTime() + 30 * 60 * 1000); // 30 minutes from now
    if (startDateTime < minimumStartTime) {
      return 'Start time must be at least 30 minutes from now';
    }

    if (endDateTime <= startDateTime) {
      return 'End time must be after start time';
    }

    const duration = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60); // minutes
    if (duration < 30) {
      return 'Minimum slot duration is 30 minutes';
    }

    if (duration > 480) { // 8 hours
      return 'Maximum slot duration is 8 hours';
    }

    return null;
  };

  const handleAddTimeSlot = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateTimeSlot(newSlot);
    if (validationError) {
      showMessage(validationError, true);
      return;
    }

    try {
      const startDateTime = new Date(`${newSlot.date}T${newSlot.startTime}`);
      const endDateTime = new Date(`${newSlot.date}T${newSlot.endTime}`);
      
      await apiService.timeSlots.createBatch([{
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        amount: newSlot.amount,
        currency: newSlot.currency,
      }]);
      
      setNewSlot({ date: '', startTime: '', endTime: '', amount: 50, currency: 'USD' });
      showMessage('Time slot created successfully!');
      loadTimeSlots();
    } catch (err: any) {
      showMessage(err.response?.data?.Error || err.message || 'Failed to create time slot', true);
    }
  };

  const handleBulkAdd = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all slots
    for (let i = 0; i < bulkSlots.length; i++) {
      const validationError = validateTimeSlot(bulkSlots[i]);
      if (validationError) {
        showMessage(`Slot ${i + 1}: ${validationError}`, true);
        return;
      }
    }

    try {
      const slotsToCreate = bulkSlots.map(slot => {
        const startDateTime = new Date(`${slot.date}T${slot.startTime}`);
        const endDateTime = new Date(`${slot.date}T${slot.endTime}`);
        return {
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          amount: slot.amount,
          currency: slot.currency,
        };
      });

      await apiService.timeSlots.createBatch(slotsToCreate);
      
      setBulkSlots([{ date: '', startTime: '', endTime: '', amount: 50, currency: 'USD' }]);
      showMessage(`${slotsToCreate.length} time slots created successfully!`);
      loadTimeSlots();
    } catch (err: any) {
      showMessage(err.response?.data?.Error || err.message || 'Failed to create time slots', true);
    }
  };

  const generateRecurringSlots = (): NewSlot[] => {
    const slots: NewSlot[] = [];
    const startDate = new Date(recurringConfig.startDate);
    const endDate = new Date(recurringConfig.endDate);
    
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      if (recurringConfig.daysOfWeek.includes(currentDate.getDay())) {
        slots.push({
          date: currentDate.toISOString().split('T')[0],
          startTime: recurringConfig.startTime,
          endTime: recurringConfig.endTime,
          amount: recurringConfig.amount,
          currency: recurringConfig.currency,
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return slots;
  };

  const handleRecurringAdd = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recurringConfig.startDate || !recurringConfig.endDate || 
        !recurringConfig.startTime || !recurringConfig.endTime || 
        recurringConfig.daysOfWeek.length === 0) {
      showMessage('All fields are required for recurring slots', true);
      return;
    }

    const slotsToCreate = generateRecurringSlots();
    
    if (slotsToCreate.length === 0) {
      showMessage('No slots generated with the selected configuration', true);
      return;
    }

    if (slotsToCreate.length > 50) {
      showMessage('Maximum 50 slots can be created at once', true);
      return;
    }

    // Validate generated slots
    for (let i = 0; i < slotsToCreate.length; i++) {
      const validationError = validateTimeSlot(slotsToCreate[i]);
      if (validationError) {
        showMessage(`Generated slot ${i + 1}: ${validationError}`, true);
        return;
      }
    }

    try {
      const apiSlots = slotsToCreate.map(slot => {
        const startDateTime = new Date(`${slot.date}T${slot.startTime}`);
        const endDateTime = new Date(`${slot.date}T${slot.endTime}`);
        return {
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          amount: slot.amount,
          currency: slot.currency,
        };
      });

      await apiService.timeSlots.createBatch(apiSlots);
      
      setRecurringConfig({
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        amount: 50,
        currency: 'USD',
        daysOfWeek: [],
      });
      showMessage(`${apiSlots.length} recurring time slots created successfully!`);
      loadTimeSlots();
    } catch (err: any) {
      showMessage(err.response?.data?.Error || err.message || 'Failed to create recurring time slots', true);
    }
  };

  const addBulkSlot = () => {
    setBulkSlots([...bulkSlots, { date: '', startTime: '', endTime: '', amount: 50, currency: 'USD' }]);
  };

  const removeBulkSlot = (index: number) => {
    if (bulkSlots.length > 1) {
      setBulkSlots(bulkSlots.filter((_, i) => i !== index));
    }
  };

  const updateBulkSlot = (index: number, field: keyof NewSlot, value: string | number) => {
    const updated = [...bulkSlots];
    (updated[index] as any)[field] = value;
    setBulkSlots(updated);
  };

  const handleDeleteTimeSlot = async (slotId: number) => {
    if (!confirm('Are you sure you want to delete this time slot?')) return;
    
    try {
      await apiService.timeSlots.delete(slotId);
      showMessage('Time slot deleted successfully!');
      loadTimeSlots();
    } catch (err: any) {
      showMessage(err.response?.data?.Error || err.message || 'Failed to delete time slot', true);
    }
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    const locale = 'en-US';
    return date.toLocaleString(locale, {
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: locale === 'en-US'
    });
  };

  const getMinDate = () => {
    // Allow today's date since we now allow time slots starting from 30 minutes from now
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-stitch-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stitch-canvas">
      <div className="border-b border-stitch-border bg-gradient-to-br from-stitch-success-soft via-stitch-surface to-stitch-primary-soft">
        <div className="container mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-stitch-success">
            <span className="material-symbols-outlined text-base">calendar_clock</span>
            {t('timeSlots.heroBadge', 'Availability')}
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-stitch-fg md:text-4xl">
            {t('timeSlots.management', 'Time Slot Management')}
          </h1>
          <p className="mt-2 text-stitch-muted">{t('timeSlots.subtitle', 'Manage your availability for interviews')}</p>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Success Message */}
      {success && (
        <div className="mb-6 border-l-4 border-stitch-success bg-stitch-success-soft p-4">
          <div className="flex gap-3">
            <span
              className="material-symbols-outlined flex-shrink-0 text-stitch-success"
              style={{ fontVariationSettings: "'wght' 500" }}
            >
              check_circle
            </span>
            <p className="text-stitch-fg">{success}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 border-l-4 border-stitch-danger bg-stitch-danger-soft p-4">
          <div className="flex gap-3">
            <span
              className="material-symbols-outlined flex-shrink-0 text-stitch-danger"
              style={{ fontVariationSettings: "'wght' 500" }}
            >
              error
            </span>
            <p className="text-stitch-fg">{error}</p>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
        <h2 className="mb-4 text-xl font-semibold text-stitch-fg">
          {t('timeSlots.manage', 'Time slots')}
        </h2>

        <TabGroup selectedIndex={formTab} onChange={setFormTab}>
          <TabList className="mb-4 flex flex-wrap gap-2 border-b border-stitch-border pb-3">
            <Tab
              className={({ selected }) =>
                `rounded-stitch-lg px-4 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-stitch-ring ${
                  selected
                    ? 'bg-stitch-primary text-white'
                    : 'text-stitch-fg-soft hover:bg-stitch-surface-2'
                }`
              }
            >
              {t('timeSlots.tabSingle', 'Single slot')}
            </Tab>
            <Tab
              className={({ selected }) =>
                `rounded-stitch-lg px-4 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-stitch-ring ${
                  selected
                    ? 'bg-stitch-success text-white'
                    : 'text-stitch-fg-soft hover:bg-stitch-surface-2'
                }`
              }
            >
              {t('timeSlots.tabBulk', 'Multiple')}
            </Tab>
            <Tab
              className={({ selected }) =>
                `rounded-stitch-lg px-4 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-stitch-ring ${
                  selected
                    ? 'bg-stitch-accent text-white'
                    : 'text-stitch-fg-soft hover:bg-stitch-surface-2'
                }`
              }
            >
              {t('timeSlots.tabRecurring', 'Recurring')}
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
          <form onSubmit={handleAddTimeSlot} className="mb-6 rounded-stitch-lg bg-stitch-surface-2 p-4">
            <h3 className="text-lg font-medium mb-3">{t('timeSlots.addSingleTitle', 'Add Single Time Slot')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={newSlot.date}
                  min={getMinDate()}
                  onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={newSlot.startTime}
                  onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={newSlot.endTime}
                  onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newSlot.amount || ''}
                  onChange={(e) => setNewSlot({ ...newSlot, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                  placeholder="50.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Currency
                </label>
                <select
                  value={newSlot.currency || 'USD'}
                  onChange={(e) => setNewSlot({ ...newSlot, currency: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="GEL">GEL</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <button
                type="submit"
                className="rounded-stitch-md bg-stitch-success px-6 py-2 text-white transition-colors hover:opacity-90"
              >
                {t('timeSlots.addTimeSlot', 'Add Time Slot')}
              </button>
            </div>
          </form>
            </TabPanel>

            <TabPanel>
          <form onSubmit={handleBulkAdd} className="mb-6 rounded-stitch-lg bg-stitch-surface-2 p-4">
            <h3 className="text-lg font-medium mb-3">{t('timeSlots.addMultipleTitle', 'Add Multiple Time Slots')}</h3>
            {bulkSlots.map((slot, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={slot.date}
                    min={getMinDate()}
                    onChange={(e) => updateBulkSlot(index, 'date', e.target.value)}
                    className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={slot.startTime}
                    onChange={(e) => updateBulkSlot(index, 'startTime', e.target.value)}
                    className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={slot.endTime}
                    onChange={(e) => updateBulkSlot(index, 'endTime', e.target.value)}
                    className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                    Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={slot.amount || ''}
                    onChange={(e) => updateBulkSlot(index, 'amount', parseFloat(e.target.value) || 0)}
                    className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                    placeholder="50.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                    Currency
                  </label>
                  <select
                    value={slot.currency || 'USD'}
                    onChange={(e) => updateBulkSlot(index, 'currency', e.target.value)}
                    className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-primary/30"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="GEL">GEL</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  {bulkSlots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBulkSlot(index)}
                      className="rounded-stitch-md border border-stitch-danger/40 px-3 py-2 text-stitch-danger hover:bg-stitch-danger-soft"
                    >
                      Remove
                    </button>
                  )}
                  {index === bulkSlots.length - 1 && (
                    <button
                      type="button"
                      onClick={addBulkSlot}
                      className="rounded-stitch-md border border-stitch-primary/40 px-3 py-2 text-stitch-primary hover:bg-stitch-primary-soft"
                    >
                      Add More
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div className="mt-4">
              <button
                type="submit"
                className="rounded-stitch-md bg-stitch-success px-6 py-2 text-white transition-colors hover:opacity-90"
              >
                {t('timeSlots.addAllSlots', 'Add all time slots ({{count}})', { count: bulkSlots.length })}
              </button>
            </div>
          </form>
            </TabPanel>

            <TabPanel>
          <form onSubmit={handleRecurringAdd} className="mb-6 rounded-stitch-lg bg-stitch-surface-2 p-4">
            <h3 className="text-lg font-medium mb-3">{t('timeSlots.addRecurringTitle', 'Add Recurring Time Slots')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={recurringConfig.startDate}
                  min={getMinDate()}
                  onChange={(e) => setRecurringConfig({ ...recurringConfig, startDate: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-accent/30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={recurringConfig.endDate}
                  min={recurringConfig.startDate || getMinDate()}
                  onChange={(e) => setRecurringConfig({ ...recurringConfig, endDate: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-accent/30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={recurringConfig.startTime}
                  onChange={(e) => setRecurringConfig({ ...recurringConfig, startTime: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-accent/30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={recurringConfig.endTime}
                  onChange={(e) => setRecurringConfig({ ...recurringConfig, endTime: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-accent/30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={recurringConfig.amount || ''}
                  onChange={(e) => setRecurringConfig({ ...recurringConfig, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-accent/30"
                  placeholder="50.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stitch-fg-soft mb-1">
                  Currency
                </label>
                <select
                  value={recurringConfig.currency || 'USD'}
                  onChange={(e) => setRecurringConfig({ ...recurringConfig, currency: e.target.value })}
                  className="w-full rounded-stitch-md border border-stitch-border bg-stitch-surface px-3 py-2 text-sm text-stitch-fg focus:outline-none focus:ring-2 focus:ring-stitch-accent/30"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="GEL">GEL</option>
                </select>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-stitch-fg-soft mb-2">
                Days of Week
              </label>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                {daysOfWeekNames.map((day, index) => (
                  <label key={index} className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={recurringConfig.daysOfWeek.includes(index)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRecurringConfig({
                            ...recurringConfig,
                            daysOfWeek: [...recurringConfig.daysOfWeek, index].sort()
                          });
                        } else {
                          setRecurringConfig({
                            ...recurringConfig,
                            daysOfWeek: recurringConfig.daysOfWeek.filter(d => d !== index)
                          });
                        }
                      }}
                      className="mr-1"
                    />
                    <span className="text-sm">{day.slice(0, 3)}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {recurringConfig.startDate && recurringConfig.endDate && recurringConfig.daysOfWeek.length > 0 && (
              <div className="mb-4 rounded-stitch-md bg-stitch-primary-soft p-3">
                <p className="text-sm text-stitch-fg-soft">
                  This will create {generateRecurringSlots().length} time slots
                </p>
              </div>
            )}
            
            <div className="mt-4">
              <button
                type="submit"
                className="rounded-stitch-md bg-stitch-accent px-6 py-2 text-white transition-colors hover:opacity-90"
              >
                {t('timeSlots.addRecurring', 'Add Recurring Slots')}
              </button>
            </div>
          </form>
            </TabPanel>
          </TabPanels>
        </TabGroup>

        {/* Time Slots List */}
        <div className="space-y-4">
          {timeSlots.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-stitch-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-stitch-fg">No time slots</h3>
              <p className="mt-1 text-sm text-stitch-muted">Get started by creating your first time slot.</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-stitch-muted">
                  Total: {timeSlots.length} slots | 
                  Available: {timeSlots.filter(slot => slot.isAvailable).length} | 
                  Booked: {timeSlots.filter(slot => !slot.isAvailable).length}
                </p>
              </div>
              {timeSlots.map((slot) => (
                <div
                  key={slot.id}
                  className="flex items-center justify-between rounded-stitch-xl border border-stitch-border p-4 transition-colors hover:bg-stitch-surface-2"
                >
                  <div className="flex-1">
                    <div className="font-medium text-stitch-fg">
                      {formatDateTime(slot.startTime)} - {formatDateTime(slot.endTime)}
                    </div>
                    <div className="text-sm text-stitch-muted mt-1 space-y-1">
                      <div>
                        Status: {slot.isAvailable ? (
                          <span className="font-medium text-stitch-success">Available</span>
                        ) : (
                          <span className="font-medium text-stitch-danger">Booked</span>
                        )}
                      </div>
                      {slot.amount && (
                        <div>
                          Price: <span className="font-medium text-stitch-primary">
                            {slot.currency || 'USD'} {slot.amount.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {slot.isAvailable && (
                      <button
                        onClick={() => handleDeleteTimeSlot(slot.id)}
                        className="rounded-stitch-md p-2 text-stitch-danger transition-colors hover:bg-stitch-danger-soft hover:text-stitch-danger"
                        title="Delete time slot"
                      >
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L7.586 12l-1.293 1.293a1 1 0 101.414 1.414L9 13.414l2.293 2.293a1 1 0 001.414-1.414L11.414 12l1.293-1.293z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
} 